import { DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { AlertController } from '@ionic/angular/lazy';
import { FloatingButtonSettingsService } from '../../service/FloatingButtonSettings/floating-button-settings.service';
import {
  CapturedImage,
  ContinuousCameraConfig,
  ContinuousCameraDeleteHandler,
  ContinuousCameraSaveHandler,
  DEFAULT_CONTINUOUS_CAMERA_CONFIG,
  OutputResolution,
} from './continuous-camera.types';

interface ResolutionOption {
  id: string;
  label: string;
  value: OutputResolution | 'max';
}

interface CropRect { x: number; y: number; width: number; height: number; }

type ThumbnailStatus = 'processing' | 'saving' | 'saved' | 'error';

interface CameraThumbnail {
  id: number;
  status: ThumbnailStatus;
  image?: CapturedImage;
  url?: string;
  error?: string;
}

@Component({
  standalone: false,
  selector: 'app-continuous-camera',
  templateUrl: './continuous-camera.component.html',
  styleUrls: ['./continuous-camera.component.scss'],
})
export class ContinuousCameraComponent implements AfterViewInit, OnDestroy {
  @Input() config: ContinuousCameraConfig = {};
  @Input() saveImage?: ContinuousCameraSaveHandler;
  @Input() deleteImage?: ContinuousCameraDeleteHandler;
  @Output() closed = new EventEmitter<CapturedImage[]>();
  @ViewChild('video') video?: ElementRef<HTMLVideoElement>;
  @ViewChild('previewCanvas') previewCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('previewViewport') previewViewport?: ElementRef<HTMLElement>;
  @ViewChild('thumbnailStrip') thumbnailStrip?: ElementRef<HTMLElement>;
  @ViewChild('photoSlider') photoSlider?: ElementRef<HTMLElement>;

  readonly cameraResolutionOptions: ResolutionOption[] = [
    { id: 'max', label: '最大（端末対応範囲）', value: 'max' },
    { id: '4k', label: '3840 × 2160', value: { width: 3840, height: 2160 } },
    { id: '1440p', label: '2560 × 1440', value: { width: 2560, height: 1440 } },
    { id: '1080p', label: '1920 × 1080', value: { width: 1920, height: 1080 } },
  ];
  readonly outputResolutionOptions: ResolutionOption[] = [
    { id: '1080p', label: '1920 × 1080', value: { width: 1920, height: 1080 } },
    { id: '1440p', label: '2560 × 1440', value: { width: 2560, height: 1440 } },
    { id: '4k', label: '3840 × 2160', value: { width: 3840, height: 2160 } },
    { id: 'source', label: '入力解像度のまま', value: 'source' },
  ];

  settings = { ...DEFAULT_CONTINUOUS_CAMERA_CONFIG };
  stream?: MediaStream;
  isStarting = true;
  isActive = false;
  isSettingsOpen = false;
  errorMessage = '';
  captureLimitMessage = '';
  capturedImages: CapturedImage[] = [];
  thumbnails: CameraThumbnail[] = [];
  selectedThumbnail?: CameraThumbnail;
  isCapturing = false;
  isFinishing = false;
  isDeleting = false;
  isShutterAnimating = false;
  sourceWidth = 0;
  sourceHeight = 0;
  zoom = 1;
  panX = 0;
  panY = 0;

  private animationFrame?: number;
  private resizeObserver?: ResizeObserver;
  private pointerPositions = new Map<number, { x: number; y: number }>();
  private dragStart?: { x: number; y: number; panX: number; panY: number };
  private pinchStart?: { distance: number; zoom: number };
  private nextThumbnailId = 1;
  private shutterAnimationTimer?: ReturnType<typeof setTimeout>;
  private finishTimer?: ReturnType<typeof setTimeout>;
  private readonly pendingWork = new Set<Promise<void>>();
  private portalAnchor?: Comment;
  private releaseFloatingButtonSuppression?: () => void;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private alertController: AlertController,
    public floatingButtonSettings: FloatingButtonSettingsService,
    private hostElement: ElementRef<HTMLElement>,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.releaseFloatingButtonSuppression = this.floatingButtonSettings.suppress('continuous-camera');
  }

  ngAfterViewInit(): void {
    this.moveHostToDocumentBody();
    this.settings = { ...DEFAULT_CONTINUOUS_CAMERA_CONFIG, ...this.config };
    this.resizeObserver = new ResizeObserver(() => this.resizePreview());
    if (this.previewViewport?.nativeElement) this.resizeObserver.observe(this.previewViewport.nativeElement);
    void this.startCamera();
  }

  ngOnDestroy(): void {
    this.stopCamera();
    this.resizeObserver?.disconnect();
    if (this.shutterAnimationTimer) clearTimeout(this.shutterAnimationTimer);
    if (this.finishTimer) clearTimeout(this.finishTimer);
    this.revokeThumbnailUrls();
    this.releaseFloatingButtonSuppression?.();
    this.restoreHostPosition();
  }

  get outputSize(): { width: number; height: number } {
    if (this.settings.outputResolution === 'source') return { width: this.sourceWidth || 1920, height: this.sourceHeight || 1080 };
    const requested = this.settings.outputResolution;
    const viewport = this.previewViewport?.nativeElement;
    // 縦持ちでは長辺・短辺を入れ替え、プレビューと保存画像の向きを一致させる。
    return viewport && viewport.clientHeight > viewport.clientWidth
      ? { width: requested.height, height: requested.width }
      : requested;
  }

  get maxZoom(): number {
    if (!this.settings.enableSoftwareZoom || !this.sourceWidth || !this.sourceHeight) return 1;
    const output = this.outputSize;
    return Math.max(1, Math.min(this.sourceWidth / output.width, this.sourceHeight / output.height));
  }

  get canCapture(): boolean {
    const limit = this.settings.maxCaptures;
    return this.isActive && !this.isStarting && !this.isCapturing && !this.isFinishing
      && (limit == null || this.thumbnails.length < limit);
  }

  async startCamera(): Promise<void> {
    this.stopCamera();
    this.isStarting = true;
    this.errorMessage = '';
    this.captureLimitMessage = '';
    try {
      this.stream = await this.openBestAvailableStream();
      const video = this.video?.nativeElement;
      if (!video) throw new Error('プレビューを初期化できませんでした。');
      video.srcObject = this.stream;
      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error('カメラプレビューを開始できませんでした。'));
      });
      // iOS PWAでは、stream自体は開始済みでも play() のPromiseが解決しないことがある。
      // 操作UIを永久に起動中にしないため、再生要求は送るが完了待ちはしない。
      void video.play().catch(error => console.warn('カメラプレビューの再生要求に失敗しました', error));
      this.sourceWidth = video.videoWidth;
      this.sourceHeight = video.videoHeight;
      this.zoom = 1;
      this.panX = 0;
      this.panY = 0;
      this.isActive = true;
      this.resizePreview();
      this.renderPreview();
    } catch (error) {
      this.errorMessage = this.toCameraErrorMessage(error);
      this.stopCamera();
    } finally {
      this.isStarting = false;
      this.changeDetector.detectChanges();
    }
  }

  async finish(): Promise<void> {
    if (this.isFinishing) return;
    this.isFinishing = true;
    while (this.pendingWork.size) await Promise.allSettled([...this.pendingWork]);
    const failures = this.thumbnails.filter(thumbnail => thumbnail.status === 'error').length;
    if (failures) {
      this.captureLimitMessage = `保存できなかった写真が${failures}枚あります。確認して削除してください。`;
      this.isFinishing = false;
      this.changeDetector.detectChanges();
      return;
    }
    const results = this.thumbnails.flatMap(thumbnail => thumbnail.image ? [thumbnail.image] : []);
    this.stopCamera();
    this.closed.emit(results);
  }

  capture(): void {
    if (!this.canCapture) {
      if (this.settings.maxCaptures != null) this.captureLimitMessage = `最大${this.settings.maxCaptures}枚です。`;
      return;
    }
    const video = this.video?.nativeElement;
    if (!video) return;
    this.isCapturing = true;
    this.playShutterAnimation();
    const thumbnail: CameraThumbnail = { id: this.nextThumbnailId++, status: 'processing' };
    this.thumbnails = [...this.thumbnails, thumbnail];
    this.scrollThumbnailsToEnd();
    const work = this.completeCapture(thumbnail, video);
    this.trackWork(work, thumbnail);
  }

  openPreview(thumbnail: CameraThumbnail): void {
    if (thumbnail.status === 'processing') return;
    this.selectedThumbnail = thumbnail;
    requestAnimationFrame(() => {
      const slider = this.photoSlider?.nativeElement;
      const index = this.previewPosition() - 1;
      if (slider && index >= 0) slider.scrollLeft = slider.clientWidth * index;
    });
  }

  closePreview(): void {
    this.selectedThumbnail = undefined;
  }

  previewPosition(): number {
    if (!this.selectedThumbnail) return 0;
    const index = this.previewableThumbnails.findIndex(item => item.id === this.selectedThumbnail?.id);
    return index < 0 ? 0 : index + 1;
  }

  get previewableThumbnails(): CameraThumbnail[] {
    return this.thumbnails.filter(thumbnail => thumbnail.status !== 'processing');
  }

  onPreviewScroll(event: Event): void {
    const slider = event.currentTarget as HTMLElement;
    if (!slider.clientWidth) return;
    const thumbnail = this.previewableThumbnails[Math.round(slider.scrollLeft / slider.clientWidth)];
    if (thumbnail) this.selectedThumbnail = thumbnail;
  }

  async deleteSelected(): Promise<void> {
    const thumbnail = this.selectedThumbnail;
    if (!thumbnail || this.isDeleting) return;
    const alert = await this.alertController.create({
      header: '写真を削除',
      message: 'この写真を削除しますか？',
      buttons: [{ text: 'キャンセル', role: 'cancel' }, { text: '削除', role: 'destructive' }],
    });
    await alert.present();
    const result = await alert.onDidDismiss();
    if (result.role !== 'destructive') return;

    this.isDeleting = true;
    try {
      if (thumbnail.status === 'saved' && this.deleteImage && thumbnail.image) await this.deleteImage(thumbnail.image);
      this.removeThumbnail(thumbnail);
    } catch (error) {
      thumbnail.error = this.errorText(error, '写真を削除できませんでした。');
      this.changeDetector.detectChanges();
    } finally {
      this.isDeleting = false;
    }
  }

  trackThumbnail(_index: number, thumbnail: CameraThumbnail): number {
    return thumbnail.id;
  }

  private async completeCapture(thumbnail: CameraThumbnail, video: HTMLVideoElement): Promise<void> {
    const output = this.outputSize;
    const canvas = document.createElement('canvas');
    canvas.width = output.width;
    canvas.height = output.height;
    const context = canvas.getContext('2d');
    if (!context) {
      this.markThumbnailError(thumbnail, '撮影用Canvasを作成できませんでした。');
      this.isCapturing = false;
      return;
    }
    const crop = this.currentCrop();
    context.drawImage(video, crop.x, crop.y, crop.width, crop.height, 0, 0, output.width, output.height);
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', this.normalizedJpegQuality()));
    if (!blob) {
      this.markThumbnailError(thumbnail, 'JPEG画像を生成できませんでした。');
      this.isCapturing = false;
      return;
    }
    const image: CapturedImage = {
      blob, width: output.width, height: output.height, mimeType: 'image/jpeg', size: blob.size,
      capturedAt: new Date().toISOString(), zoom: this.zoom,
    };
    thumbnail.image = image;
    thumbnail.url = URL.createObjectURL(blob);
    thumbnail.status = this.saveImage ? 'saving' : 'saved';
    this.capturedImages = [...this.capturedImages, image];
    this.isCapturing = false;
    this.captureLimitMessage = this.settings.maxCaptures != null && this.thumbnails.length >= this.settings.maxCaptures
      ? `最大${this.settings.maxCaptures}枚に到達しました。` : '';
    if (this.settings.shutterSound) this.playShutterSound();
    this.changeDetector.detectChanges();
    if (this.saveImage) {
      try {
        const saved = await this.saveImage(image);
        if (saved?.storageId) image.storageId = saved.storageId;
        thumbnail.status = 'saved';
      } catch (error) {
        this.markThumbnailError(thumbnail, this.errorText(error, '写真を保存できませんでした。'));
      }
    }
    this.changeDetector.detectChanges();
    // 現在の処理をpendingWorkから外した後に終了する。ここでawaitすると自分自身を待ってしまう。
    if (!this.settings.continuous && thumbnail.status === 'saved') {
      this.finishTimer = setTimeout(() => void this.finish());
    }
  }

  async onCameraResolutionChanged(value: string | number | null | undefined): Promise<void> {
    const selected = this.cameraResolutionOptions.find(option => option.id === value);
    if (!selected || (typeof selected.value === 'string' && selected.value !== 'max')) return;
    this.settings.cameraResolution = selected.value;
    await this.startCamera();
  }

  onOutputResolutionChanged(value: string | number | null | undefined): void {
    const selected = this.outputResolutionOptions.find(option => option.id === value);
    if (!selected || selected.value === 'max') return;
    this.settings.outputResolution = selected.value;
    this.zoom = Math.min(this.zoom, this.maxZoom);
    this.panX = 0;
    this.panY = 0;
    this.resizePreview();
  }

  onZoomInput(value: unknown): void {
    if (typeof value !== 'number' && typeof value !== 'string') return;
    const next = Number(value);
    if (!Number.isFinite(next)) return;
    this.zoom = this.clamp(next, 1, this.maxZoom);
    this.panX = this.clamp(this.panX, -1, 1);
    this.panY = this.clamp(this.panY, -1, 1);
  }

  onSoftwareZoomChanged(): void {
    if (!this.settings.enableSoftwareZoom) {
      this.zoom = 1;
      this.panX = 0;
      this.panY = 0;
    }
  }

  onPointerDown(event: PointerEvent): void {
    this.previewCanvas?.nativeElement.setPointerCapture(event.pointerId);
    this.pointerPositions.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointerPositions.size === 1) {
      this.dragStart = { x: event.clientX, y: event.clientY, panX: this.panX, panY: this.panY };
      this.pinchStart = undefined;
    } else if (this.pointerPositions.size === 2) {
      this.pinchStart = { distance: this.pointerDistance(), zoom: this.zoom };
      this.dragStart = undefined;
    }
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.pointerPositions.has(event.pointerId)) return;
    this.pointerPositions.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointerPositions.size >= 2 && this.pinchStart && this.settings.enableSoftwareZoom) {
      const distance = this.pointerDistance();
      if (this.pinchStart.distance > 0) this.zoom = this.clamp(this.pinchStart.zoom * distance / this.pinchStart.distance, 1, this.maxZoom);
      return;
    }
    if (!this.dragStart || this.zoom <= 1 || !this.settings.enableSoftwareZoom) return;
    const rect = this.previewCanvas?.nativeElement.getBoundingClientRect();
    if (!rect) return;
    this.panX = this.clamp(this.dragStart.panX - (event.clientX - this.dragStart.x) / (rect.width / 2), -1, 1);
    this.panY = this.clamp(this.dragStart.panY - (event.clientY - this.dragStart.y) / (rect.height / 2), -1, 1);
  }

  onPointerEnd(event: PointerEvent): void {
    this.pointerPositions.delete(event.pointerId);
    if (this.pointerPositions.size === 1) {
      const point = [...this.pointerPositions.values()][0];
      this.dragStart = { x: point.x, y: point.y, panX: this.panX, panY: this.panY };
    } else {
      this.dragStart = undefined;
      this.pinchStart = undefined;
    }
  }

  cameraResolutionId(): string {
    const resolution = this.settings.cameraResolution;
    if (resolution === 'max') return 'max';
    return this.cameraResolutionOptions.find(option => typeof option.value !== 'string' && option.value.width === resolution.width && option.value.height === resolution.height)?.id ?? 'max';
  }

  outputResolutionId(): string {
    const resolution = this.settings.outputResolution;
    if (resolution === 'source') return 'source';
    return this.outputResolutionOptions.find(option => typeof option.value !== 'string' && option.value.width === resolution.width && option.value.height === resolution.height)?.id ?? '1080p';
  }

  private async openBestAvailableStream(): Promise<MediaStream> {
    const requested = this.settings.cameraResolution;
    const sizes = requested === 'max'
      ? [{ width: 3840, height: 2160 }, { width: 2560, height: 1440 }, { width: 1920, height: 1080 }, undefined]
      : [requested, undefined];
    let lastError: unknown;
    for (const size of sizes) {
      try {
        const video: MediaTrackConstraints = { facingMode: { ideal: 'environment' } };
        if (size) {
          video.width = { ideal: size.width };
          video.height = { ideal: size.height };
        }
        return await navigator.mediaDevices.getUserMedia({ audio: false, video });
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError ?? new Error('カメラを開始できませんでした。');
  }

  private currentCrop(): CropRect {
    const output = this.outputSize;
    const sourceAspect = this.sourceWidth / this.sourceHeight;
    const outputAspect = output.width / output.height;
    let baseWidth: number;
    let baseHeight: number;
    if (sourceAspect > outputAspect) {
      baseHeight = this.sourceHeight;
      baseWidth = baseHeight * outputAspect;
    } else {
      baseWidth = this.sourceWidth;
      baseHeight = baseWidth / outputAspect;
    }
    const width = baseWidth / this.zoom;
    const height = baseHeight / this.zoom;
    const offsetX = (this.sourceWidth - width) / 2;
    const offsetY = (this.sourceHeight - height) / 2;
    return {
      x: this.clamp(this.sourceWidth / 2 + this.panX * offsetX - width / 2, 0, this.sourceWidth - width),
      y: this.clamp(this.sourceHeight / 2 + this.panY * offsetY - height / 2, 0, this.sourceHeight - height),
      width, height,
    };
  }

  private renderPreview = (): void => {
    const canvas = this.previewCanvas?.nativeElement;
    const video = this.video?.nativeElement;
    if (!canvas || !video || !this.isActive) return;
    const context = canvas.getContext('2d');
    if (context && this.sourceWidth && this.sourceHeight) {
      const crop = this.cropForAspect(canvas.width / canvas.height);
      context.drawImage(video, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
    }
    this.animationFrame = requestAnimationFrame(this.renderPreview);
  };

  private resizePreview(): void {
    const canvas = this.previewCanvas?.nativeElement;
    const viewport = this.previewViewport?.nativeElement;
    if (!canvas || !viewport) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.round(viewport.clientWidth * ratio));
    canvas.height = Math.max(1, Math.round(viewport.clientHeight * ratio));
  }

  private moveHostToDocumentBody(): void {
    const host = this.hostElement.nativeElement;
    const parent = host.parentNode;
    if (!parent || !this.document.body || parent === this.document.body) return;
    // Ionicのmodal/router内にあるtransformやサイズ制限を受けないよう、表示中だけbody直下へ退避する。
    this.portalAnchor = this.document.createComment('continuous-camera-anchor');
    parent.insertBefore(this.portalAnchor, host);
    this.document.body.appendChild(host);
  }

  private restoreHostPosition(): void {
    const anchor = this.portalAnchor;
    if (!anchor?.parentNode) {
      if (anchor) this.hostElement.nativeElement.remove();
      this.portalAnchor = undefined;
      return;
    }
    anchor.parentNode.insertBefore(this.hostElement.nativeElement, anchor.nextSibling);
    anchor.remove();
    this.portalAnchor = undefined;
  }

  private trackWork(work: Promise<void>, thumbnail: CameraThumbnail): void {
    this.pendingWork.add(work);
    void work.then(() => {
      this.pendingWork.delete(work);
      this.changeDetector.detectChanges();
    }, error => {
      this.pendingWork.delete(work);
      this.isCapturing = false;
      this.markThumbnailError(thumbnail, this.errorText(error, '写真を生成できませんでした。'));
    });
  }

  private playShutterAnimation(): void {
    if (this.shutterAnimationTimer) clearTimeout(this.shutterAnimationTimer);
    this.isShutterAnimating = false;
    requestAnimationFrame(() => {
      this.isShutterAnimating = true;
      this.changeDetector.detectChanges();
      this.shutterAnimationTimer = setTimeout(() => {
        this.isShutterAnimating = false;
        this.changeDetector.detectChanges();
      }, 360);
    });
  }

  private scrollThumbnailsToEnd(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const strip = this.thumbnailStrip?.nativeElement;
      strip?.scrollTo({ left: strip.scrollWidth, behavior: 'smooth' });
    }));
  }

  private markThumbnailError(thumbnail: CameraThumbnail, message: string): void {
    thumbnail.status = 'error';
    thumbnail.error = message;
    this.errorMessage = message;
    this.changeDetector.detectChanges();
  }

  private removeThumbnail(thumbnail: CameraThumbnail): void {
    if (thumbnail.url) URL.revokeObjectURL(thumbnail.url);
    this.thumbnails = this.thumbnails.filter(item => item.id !== thumbnail.id);
    if (thumbnail.image) this.capturedImages = this.capturedImages.filter(image => image !== thumbnail.image);
    this.selectedThumbnail = undefined;
    this.captureLimitMessage = '';
    if (!this.thumbnails.some(item => item.status === 'error')) this.errorMessage = '';
    this.changeDetector.detectChanges();
  }

  private revokeThumbnailUrls(): void {
    this.thumbnails.forEach(thumbnail => {
      if (thumbnail.url) URL.revokeObjectURL(thumbnail.url);
    });
  }

  private errorText(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
  }

  private cropForAspect(aspectRatio: number): CropRect {
    const sourceAspect = this.sourceWidth / this.sourceHeight;
    let baseWidth: number;
    let baseHeight: number;
    if (sourceAspect > aspectRatio) {
      baseHeight = this.sourceHeight;
      baseWidth = baseHeight * aspectRatio;
    } else {
      baseWidth = this.sourceWidth;
      baseHeight = baseWidth / aspectRatio;
    }
    const width = baseWidth / this.zoom;
    const height = baseHeight / this.zoom;
    const offsetX = (this.sourceWidth - width) / 2;
    const offsetY = (this.sourceHeight - height) / 2;
    return {
      x: this.clamp(this.sourceWidth / 2 + this.panX * offsetX - width / 2, 0, this.sourceWidth - width),
      y: this.clamp(this.sourceHeight / 2 + this.panY * offsetY - height / 2, 0, this.sourceHeight - height),
      width, height,
    };
  }

  private stopCamera(): void {
    if (this.animationFrame !== undefined) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = undefined;
    this.stream?.getTracks().forEach(track => track.stop());
    this.stream = undefined;
    const video = this.video?.nativeElement;
    if (video) video.srcObject = null;
    this.isActive = false;
    this.pointerPositions.clear();
  }

  private pointerDistance(): number {
    const [first, second] = [...this.pointerPositions.values()];
    return first && second ? Math.hypot(first.x - second.x, first.y - second.y) : 0;
  }

  private normalizedJpegQuality(): number {
    return this.clamp(Number(this.settings.jpegQuality) || DEFAULT_CONTINUOUS_CAMERA_CONFIG.jpegQuality, 0, 1);
  }

  private playShutterSound(): void {
    try {
      const AudioContextCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextCtor) return;
      const audio = new AudioContextCtor();
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      oscillator.frequency.setValueAtTime(960, audio.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(520, audio.currentTime + 0.08);
      gain.gain.setValueAtTime(0.08, audio.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + 0.09);
      oscillator.connect(gain).connect(audio.destination);
      oscillator.start();
      oscillator.stop(audio.currentTime + 0.1);
      oscillator.addEventListener('ended', () => void audio.close());
    } catch { /* 音声再生不可でも撮影は継続する。 */ }
  }

  private toCameraErrorMessage(error: unknown): string {
    const name = error instanceof DOMException ? error.name : error instanceof Error ? error.name : '';
    return ({
      NotAllowedError: 'カメラの利用が許可されていません。',
      NotFoundError: '利用可能なカメラが見つかりません。',
      NotReadableError: 'カメラを開始できませんでした。他のアプリが使用中でないか確認してください。',
      OverconstrainedError: '要求したカメラ解像度を利用できませんでした。',
    } as Record<string, string>)[name] ?? 'カメラを開始できませんでした。';
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  @HostListener('window:resize')
  onResize(): void { this.resizePreview(); }
}
